import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Search,
  X,
  Clock,
  Smile,
  User,
  Trees,
  Utensils,
  Plane,
  Trophy,
  Lightbulb,
  Heart,
  Flag,
} from 'lucide-react';
import {
  EMOJI_CATEGORIES,
  getRecentEmojis,
  saveRecentEmoji,
  EmojiItem,
} from './emojiData';
import { useLanguage } from '../../hooks/useLanguage';

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  onClose?: () => void;
  align?: 'left' | 'right';
  side?: 'top' | 'bottom';
  className?: string;
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  recent: <Clock className="h-4 w-4" />,
  smileys: <Smile className="h-4 w-4" />,
  people: <User className="h-4 w-4" />,
  nature: <Trees className="h-4 w-4" />,
  food: <Utensils className="h-4 w-4" />,
  travel: <Plane className="h-4 w-4" />,
  activities: <Trophy className="h-4 w-4" />,
  objects: <Lightbulb className="h-4 w-4" />,
  symbols: <Heart className="h-4 w-4" />,
  flags: <Flag className="h-4 w-4" />,
};

export const EmojiPicker: React.FC<EmojiPickerProps> = ({
  onSelect,
  onClose,
  align = 'right',
  side = 'top',
  className = '',
}) => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('smileys');
  const [recentEmojis, setRecentEmojis] = useState<string[]>([]);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Load recent emojis on mount
  useEffect(() => {
    setRecentEmojis(getRecentEmojis());
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Focus search input on mount
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Filtered search results
  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return null;

    const results: EmojiItem[] = [];
    const seen = new Set<string>();

    for (const category of EMOJI_CATEGORIES) {
      for (const item of category.emojis) {
        if (seen.has(item.emoji)) continue;
        const matchesName = item.name.toLowerCase().includes(query);
        const matchesKeyword = item.keywords.some((kw) => kw.toLowerCase().includes(query));
        if (matchesName || matchesKeyword) {
          results.push(item);
          seen.add(item.emoji);
        }
      }
    }
    return results;
  }, [searchQuery]);

  const handleEmojiClick = (emoji: string) => {
    const updated = saveRecentEmoji(emoji);
    setRecentEmojis(updated);
    onSelect(emoji);
  };

  const scrollToCategory = (categoryId: string) => {
    setActiveCategory(categoryId);
    const target = categoryRefs.current[categoryId];
    if (target && scrollContainerRef.current) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const positionClasses = side === 'top'
    ? 'bottom-full mb-2'
    : 'top-full mt-2';

  const alignClasses = align === 'right'
    ? 'right-0'
    : 'left-0';

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-label="Emoji Picker"
      className={`absolute ${positionClasses} ${alignClasses} w-[340px] max-w-[90vw] h-[390px] bg-card border border-border/80 rounded-2xl shadow-2xl flex flex-col z-50 select-none overflow-hidden text-card-foreground animate-in fade-in zoom-in-95 duration-150 ${className}`}
    >
      {/* 1. Header with Search Bar */}
      <div className="p-3 border-b border-border/80 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t('emoji.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-8 pl-8 pr-7 bg-muted/60 border border-border/60 rounded-xl text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* 2. Category Nav Tabs */}
      {!searchResults && (
        <div className="flex items-center justify-between px-2 py-1 border-b border-border/50 bg-muted/20 overflow-x-auto no-scrollbar">
          {recentEmojis.length > 0 && (
            <button
              type="button"
              onClick={() => scrollToCategory('recent')}
              title="Recently Used"
              aria-label="Recently Used"
              className={`p-1.5 rounded-lg transition text-xs flex items-center justify-center ${
                activeCategory === 'recent'
                  ? 'text-primary bg-primary/10 font-bold'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
              }`}
            >
              {CATEGORY_ICONS.recent}
            </button>
          )}

          {EMOJI_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => scrollToCategory(cat.id)}
              title={cat.name}
              aria-label={cat.name}
              className={`p-1.5 rounded-lg transition text-xs flex items-center justify-center ${
                activeCategory === cat.id
                  ? 'text-primary bg-primary/10 font-bold'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
              }`}
            >
              {CATEGORY_ICONS[cat.id] || cat.icon}
            </button>
          ))}
        </div>
      )}

      {/* 3. Emoji List Body */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto p-3 space-y-3.5 scroll-smooth"
      >
        {/* Search Results View */}
        {searchResults ? (
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 px-1">
              Search Results ({searchResults.length})
            </div>
            {searchResults.length === 0 ? (
              <div className="py-12 text-center text-xs text-muted-foreground">
                <p>No matching emojis found</p>
                <p className="text-[11px] opacity-70 mt-1">Try searching for words like &ldquo;happy&rdquo;, &ldquo;fire&rdquo;, or &ldquo;party&rdquo;</p>
              </div>
            ) : (
              <div className="grid grid-cols-8 gap-1">
                {searchResults.map((item) => (
                  <button
                    key={item.emoji}
                    type="button"
                    onClick={() => handleEmojiClick(item.emoji)}
                    title={item.name}
                    aria-label={item.name}
                    className="p-1.5 rounded-xl hover:bg-muted hover:scale-125 active:scale-95 transition flex items-center justify-center text-xl"
                  >
                    {item.emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Normal Category View */
          <>
            {/* Recently Used Section */}
            {recentEmojis.length > 0 && (
              <div
                ref={(el) => {
                  categoryRefs.current.recent = el;
                }}
              >
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 px-1 flex items-center gap-1.5">
                  <Clock className="h-3 w-3 text-primary" />
                  <span>{t('emoji.recentlyUsed')}</span>
                </div>
                <div className="grid grid-cols-8 gap-1">
                  {recentEmojis.map((emoji, idx) => (
                    <button
                      key={`${emoji}-${idx}`}
                      type="button"
                      onClick={() => handleEmojiClick(emoji)}
                      aria-label={`Emoji ${emoji}`}
                      className="p-1.5 rounded-xl hover:bg-muted hover:scale-125 active:scale-95 transition flex items-center justify-center text-xl"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Categorized Emojis */}
            {EMOJI_CATEGORIES.map((category) => (
              <div
                key={category.id}
                ref={(el) => {
                  categoryRefs.current[category.id] = el;
                }}
              >
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 px-1">
                  {category.name}
                </div>
                <div className="grid grid-cols-8 gap-1">
                  {category.emojis.map((item) => (
                    <button
                      key={item.emoji}
                      type="button"
                      onClick={() => handleEmojiClick(item.emoji)}
                      title={item.name}
                      aria-label={item.name}
                      className="p-1.5 rounded-xl hover:bg-muted hover:scale-125 active:scale-95 transition flex items-center justify-center text-xl"
                    >
                      {item.emoji}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
